/**
 * React Query Client Configuration
 * 
 * Centralized query client setup with:
 * - Proper staleTime to prevent aggressive refetching
 * - gcTime for cache garbage collection
 * - Wallet change invalidation helper
 */

import { QueryClient } from '@tanstack/react-query';
import { LAMPORTS_PER_SOL } from '@solana/web3.js';
import { debug } from "@/lib/debug";
import { getSOLPriceSync } from '@/hooks/useSOLPrice';

// Tiered caching system
export const CACHE_TIERS = {
  TIER_1: { staleTime: 0 },                                        // Real-time
  TIER_2: { staleTime: 30 * 1000, gcTime: 5 * 60 * 1000 },       // Semi-fresh: 30s / 5min
  TIER_3: { staleTime: 5 * 60 * 1000, gcTime: 30 * 60 * 1000 },  // Slow-changing: 5min / 30min
  TIER_4: { staleTime: 30 * 60 * 1000, gcTime: Infinity },        // Near-static: 30min / forever
} as const;

// Create the query client with optimized defaults
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Prevent aggressive refetching
      staleTime: 30 * 1000,
      gcTime: 10 * 60 * 1000,
      // Don't refetch on window focus by default
      refetchOnWindowFocus: false,
      // Don't refetch on mount if data exists
      refetchOnMount: false,
      // Retry failed requests once
      retry: 1,
      retryDelay: 1000,
    },
    mutations: {
      retry: 0,
    },
  },
});


// Query key factories for consistency
export const queryKeys = {
  // Token queries
  tokens: {
    all: ['launchedTokens'] as const,
    list: () => [...queryKeys.tokens.all, 'list'] as const,
    detail: (mint: string) => [...queryKeys.tokens.all, 'detail', mint] as const,
    stats: (mint: string) => [...queryKeys.tokens.all, 'stats', mint] as const,
  },
  // Creator queries
  creators: {
    all: ['creators'] as const,
    profile: (address: string) => [...queryKeys.creators.all, 'profile', address] as const,
    tokens: (address: string) => [...queryKeys.creators.all, 'tokens', address] as const,
    fees: (address: string) => [...queryKeys.creators.all, 'fees', address] as const,
    feeTrend: (address: string) => [...queryKeys.creators.all, 'feeTrend', address] as const,
    stats: (address: string) => [...queryKeys.creators.all, 'stats', address] as const,
  },
  // Pool/fee queries
  pools: {
    all: ['pools'] as const,
    metrics: (poolAddress: string) => [...queryKeys.pools.all, 'metrics', poolAddress] as const,
  },
  // Lists
  lists: {
    all: ['tokenLists'] as const,
    detail: (slug: string) => [...queryKeys.lists.all, 'detail', slug] as const,
  },
  // Follows
  follows: {
    following: (wallet: string) => ['follows', 'following', wallet] as const,
    followers: (address: string) => ['follows', 'followers', address] as const,
    followersList: (address: string) => ['followers-list', address] as const,
    followingList: (address: string) => ['following-list', address] as const,
  },
  // Wallet
  wallet: {
    balance: (address: string) => ['wallet', 'balance', address] as const,
    tokenBalances: (address: string) => ['wallet', 'tokenBalances', address] as const,
    portfolioValue: (address: string) => ['wallet', 'portfolioValue', address] as const,
    withdrawalAddresses: (address: string) => ['wallet', 'withdrawalAddresses', address] as const,
  },
  // Swap
  swap: {
    quote: (inputMint: string, outputMint: string, amount: string) =>
      ['swap', 'quote', inputMint, outputMint, amount] as const,
    tokenSearch: (query: string) =>
      ['swap', 'tokenSearch', query] as const,
  },
  // Price
  solPrice: ['sol-price'] as const,
  // Activity
  activity: {
    history: ['activity-history'] as const,
  },
  // Fee history
  feeHistory: (address: string) => ['fee-history', address] as const,
};

/**
 * Invalidate all user-specific cache when wallet changes
 * Call this when user switches accounts or logs out
 */
export function invalidateWalletCache(): void {
  debug.trace('[queryClient] Invalidating wallet-specific cache');
  
  // Invalidate all creator-related queries (profile, tokens, fees)
  queryClient.invalidateQueries({ queryKey: queryKeys.creators.all });
  
  // Invalidate follows
  queryClient.invalidateQueries({ queryKey: ['follows'] });
  
  // Invalidate wallet balance
  queryClient.invalidateQueries({ queryKey: ['wallet'] });
  
  // Reset all queries that might have stale user-specific data
  queryClient.removeQueries({ queryKey: queryKeys.creators.all });
}

/**
 * Invalidate all balance-related queries after an on-chain transaction
 * (trade, fee claim, etc.) to trigger near-instant UI updates.
 *
 * Phase 1: Immediate optimistic cache update using solDelta
 * Phase 2: Delayed invalidation at 2s for true on-chain values
 * Phase 3: Second invalidation at 4s as fallback for slow DAS indexing
 */
export function invalidateBalancesAfterTransaction(
  walletAddress: string | null,
  options?: { solDelta?: number }
): void {
  if (!walletAddress) return;

  const solDelta = options?.solDelta ?? 0;

  // Phase 1: Immediate optimistic cache update
  if (solDelta !== 0) {
    const solPrice = getSOLPriceSync();
    const usdDelta = solDelta * solPrice;

    // Optimistic update for portfolio value (DAS shape)
    queryClient.setQueryData(
      queryKeys.wallet.portfolioValue(walletAddress),
      (old: { solBalance: number; solValueUsd: number | null; totalValueUsd: number | null; tokens: unknown[]; emptyAccounts: unknown[] } | undefined) => {
        if (!old) return old;
        const newSolBalance = Math.max(0, old.solBalance + solDelta);
        const newSolValueUsd = newSolBalance * solPrice;
        const tokensValueUsd = (old.totalValueUsd || 0) - (old.solValueUsd || 0);
        const newTotalValueUsd = newSolValueUsd + tokensValueUsd;
        return {
          ...old,
          solBalance: newSolBalance,
          solValueUsd: newSolValueUsd,
          totalValueUsd: newTotalValueUsd > 0 ? newTotalValueUsd : null,
        };
      }
    );

    // Optimistic update for wallet SOL balance (useWalletBalance shape)
    queryClient.setQueryData(
      ['walletBalance', walletAddress] as const,
      (old: { balance: number; balanceLamports: bigint } | undefined) => {
        if (!old) return old;
        const newBalance = Math.max(0, old.balance + solDelta);
        const newLamports = BigInt(Math.max(0, Math.round(newBalance * LAMPORTS_PER_SOL)));
        return { balance: newBalance, balanceLamports: newLamports };
      }
    );
  }

  // Phase 2: Invalidate at 2s for true on-chain values
  const invalidateAll = () => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.wallet.portfolioValue(walletAddress),
    });
    queryClient.invalidateQueries({
      queryKey: ['walletBalance', walletAddress],
    });
    queryClient.invalidateQueries({
      queryKey: queryKeys.wallet.tokenBalances(walletAddress),
    });
  };

  setTimeout(invalidateAll, 2000);

  // Phase 3: Second pass at 4s for slow DAS indexing
  setTimeout(invalidateAll, 4000);
}
